import React, {useCallback, useEffect, useState} from "react";

import "./Steps.css"

interface StepsProps {
    onChangeStep: (step: number) => void
}

const textInLanguage = {
    "NO": {
        "header": "Lukeparkering",
        "button_previous": "Forrige steg",
        "button_next": "Neste steg",
        "step": "Steg",
        "disclaimer": "Informasjonen på denne nettsiden er kun ment som generell veiledning. "+
            "Brukere må selv bruke skjønn og følge gjeldende trafikkregler og føreropplæring i sitt land. "+
            "Vi tar ikke ansvar for skader, ulykker eller annet som måtte oppstå ved bruk av informasjonen.",
        "steps": [
            "Finn et ledig sted å parkere.",
            "Kjør bilen frem til den borterste bilen i luken slik at din fanger og bilens fanger står ovenfor hverandre. Sett på blinklys og sving rattet helt over.",
            "Rygg bakover helt til du kan se begge frontlysene til den bakre bilen i sidespilet.",
            "Rett opp hjulene slik at du kan rygge rett bakover og rygg helt til fronten av din bil er forbi fangeren til bilen foran.",
            "Sving rattet helt over og rygg helt til du nærmer deg bilen bak.",
            "Sving rattet helt over til den motsatte siden og kjør frem for å rette opp bilen."
        ],
    },
    "EN":{
        "header": "Parallel parking",
        "button_previous": "Previous step",
        "button_next": "Next step",
        "step": "Step",
        "disclaimer": "The information on this website is intended as general guidance only. Users must exercise their own " +
            "judgment and follow the applicable traffic laws and driver training requirements in their country. We " +
            "accept no responsibility for any damage, accidents, or other consequences resulting from the use of this " +
            "information.",
        "steps": [
            "Find an available parking spot.",
            "Drive the car up to the farthest car in the parking bay so that your bumper and the other car’s bumper are aligned. Turn on the indicator and turn the steering wheel all the way.",
            "Reverse backward until you can see both front headlights of the car behind in the side mirror.",
            "Straighten the wheels so you can reverse straight back and continue until the front of your car passes the bumper of the car in front.",
            "Turn the steering wheel all the way and reverse until you get close to the car behind.",
            "Turn the steering wheel all the way to the opposite side and drive forward to straighten the car."
        ]
    }
}

const Steps: React.FC<StepsProps> = ({onChangeStep}) => {
    const [currentStep, setCurrentStep] = useState(0)
    const [lang, setLang] = useState<"NO" | "EN">("NO");

    function toggleLang() {
        const newLang = lang === "NO" ? "EN" : "NO";
        setLang(newLang);
    }

    const onRightClick = useCallback(() => {
        if (currentStep < 5) {
            setCurrentStep(currentStep + 1);
            onChangeStep(currentStep + 1);
        }
    }, [currentStep, onChangeStep, setCurrentStep]);

    const onLeftClick = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
            onChangeStep(currentStep - 1)
        }
    }, [currentStep, onChangeStep, setCurrentStep]);

    useEffect(() => {
        const handleKeyDown = (event: any) => {
            if (event.key === "ArrowRight") {
                onRightClick();
            }
            if (event.key === "ArrowLeft") {
                onLeftClick();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onLeftClick, onRightClick]);

    useEffect(() => {
        if (window.location.hostname.endsWith('lukeparkering.no')) {
            setLang("NO");
        } else {
            setLang("EN");
        }
    },[]);

    return <div className="steps">
        <button
            className={`lang-toggle ${lang === "EN" ? "eng" : "nor"}`}
            onClick={toggleLang}
            aria-label="Bytt språk"
        >
            <span className="lang-option">NO</span>
            <span className="lang-option">EN</span>
            <span className="slider"/>
        </button>
        <button
            className="paypal-button"
            onClick={() => window.open('https://www.paypal.com/donate/?business=8LCVFDM58H4JQ&no_recurring=0&item_name=Support+the+project.+If+you+find+this+site+helpful%2C+consider+donating.&currency_code=NOK', '_blank', 'noopener')}
        >
            💛 PayPal
        </button>
        <button className="disclaimer-button">
            ℹ️
            <div className="disclaimer-tooltip">{textInLanguage[lang].disclaimer}</div>
        </button>
        <div className="step">
            <div className="header_app">
                {textInLanguage[lang].header}
            </div>
            <div className="step-header"><b>{textInLanguage[lang].step} {currentStep + 1}:</b></div>
            <div className="text-box">{textInLanguage[lang].steps[currentStep]}</div>
            <div className="buttons">
                <button className="button-17" disabled={currentStep === 0}
                        onClick={onLeftClick}
                >
                    <b>{textInLanguage[lang].button_previous}</b>
                </button>
                <button className="button-17" disabled={currentStep > 4}
                        onClick={onRightClick}
                >
                    <b>{textInLanguage[lang].button_next}</b>
                </button>
            </div>
        </div>
    </div>
}

export default Steps;
